import { TECHNIQUES, Technique, practiceShare } from '../techniques';
import { ExampleCarousel, dotColor } from './technique-example';
import { RuleId } from '../../shared/rules';

// Tips & Tricks. Every board on this page is rendered by the game's own `Board` with the game's
// own hint highlight, from a deduction the real engine found — see techniques.ts and the shared
// carousel in technique-example.tsx, which the player profiles pop up in a modal.

interface Props {
  onBack: () => void;
  /** Open the generator with this technique pre-selected as the practice target. */
  onPractice: (rule: RuleId) => void;
}

export function TechniquesView({ onBack, onPractice }: Props) {
  const navClick = (cb: () => void) => (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    cb();
  };

  return (
    <div id="view-techniques">
      <div className="tech-topbar">
        <a className="link-btn" href="/" onClick={navClick(onBack)}>← Puzzles</a>
        <h1>Tips &amp; Tricks</h1>
      </div>

      <p className="subtitle">
        Every puzzle on this site can be solved without guessing. These are the deductions that do it.
        They are the same deductions the in-game hints panel uses, written out so you can learn them
        yourself.
      </p>
      <p className="subtitle">
        Every explanation below that talks about a row applies equally to a column.
      </p>

      <nav className="tech-contents">
        {TECHNIQUES.map(t => (
          <a key={t.rule} href={`#${t.rule}`} style={{ borderColor: dotColor(t.rule) }}>{t.name}</a>
        ))}
      </nav>

      <div className="tech-legend">
        <h2>Reading the pictures</h2>
        <ul>
          <li><span className="tech-key tech-key-area" /> the rows, columns or regions it reasons about</li>
          <li><span className="tech-key tech-key-cause" /> still available — one of these holds the queen</li>
          <li><span className="tech-key tech-key-elim">✕</span> ruled out</li>
        </ul>
        <p className="tech-dim">
          Each picture loops: the bare board, what the deduction looks at, then what it kills.
          Swipe or use the arrows for two more examples.
        </p>
      </div>

      {TECHNIQUES.map(t => <TechniqueSection key={t.rule} technique={t} onPractice={onPractice} navClick={navClick} />)}

      <section className="tech-outro">
        <h2>One more thing the hints panel does</h2>
        <p>
          None of these rules catches a mark that is already wrong, so the hints panel checks separately:
          if a row, column or color has no room left for its queen, it says so and stops offering
          deductions. If the hints go quiet, look for a mistake.
        </p>
        <p className="tech-dim">
          The same rules run offline over every puzzle to work out its difficulty band and the
          techniques it exercises.
        </p>
      </section>
    </div>
  );
}

function TechniqueSection({ technique, onPractice, navClick }: {
  technique: Technique;
  onPractice: (rule: RuleId) => void;
  navClick: (cb: () => void) => (e: React.MouseEvent) => void;
}) {
  const t = technique;
  const trainable = practiceShare(t.rule) !== undefined;
  const color = dotColor(t.rule);

  return (
    <section className="tech" id={t.rule}>
      <h2 className="tech-name">
        <span className="tech-dot" style={{ background: color }} />
        {t.name}
      </h2>
      <p className="tech-tagline">{t.tagline}</p>

      <div className="tech-body">
        <ExampleCarousel technique={t} />

        <div className="tech-prose">
          {t.body.map((p, i) => <p key={i}>{p}</p>)}
          {trainable && (
            <a
              className="btn-secondary tech-practice"
              href={`/editor?practice=${t.rule}`}
              onClick={navClick(() => onPractice(t.rule as RuleId))}
            >
              🎲 Practice this
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
